import { AsyncPipe } from "@angular/common";
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  model,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import {
  ProjectFormInitialization,
  ProjectFormInitialValues,
  ProjectFormsService,
} from "@components/project-forms/project-forms.service";
import { DraegerContactPersonFormFieldComponent } from "@components/template-components/project-information/draeger-contact-person-form-field/draeger-contact-person-form-field.component";
import { ForzaIdFormFieldComponent } from "@components/template-components/project-information/forza-id-form-field/forza-id-form-field.component";
import { ForzaLinkFormFieldComponent } from "@components/template-components/project-information/forza-link-form-field/forza-link-form-field.component";
import { OpportunityLinkFormFieldComponent } from "@components/template-components/project-information/opportunity-link-form-field/opportunity-link-form-field.component";
import { PlaceNameFormFieldComponent } from "@components/template-components/project-information/place-name-form-field/place-name-form-field.component";
import type { ScopedProject } from "@domain/project/project-guards";
import { isFgdsProject, isMonitoringProject } from "@domain/project/project-guards";
import { ProjectStatusService } from "@domain/project/project-status.service";
import { ProjectService } from "@domain/project/project.service";
import { TemplateProjectDto } from "@domain/project/template";
import { ProjectStatusDto } from "@domain/project/template/property/project/details/information/project-status.dto";
import { ProjectRegion, TranslateProjectRegionPipe } from "@domain/project/translate-project-region.pipe";
import { TranslateProjectStatusPipe } from "@domain/project/translate-project-status.pipe";
import { Country, MapCountryNamePipe } from "@domain/user/locale/country";
import { UserLocaleService } from "@domain/user/locale/user-locale.service";
import { UserService } from "@domain/user/user.service";
import { AreaHeaderComponent } from "@odx/angular/components/area-header";
import { FormFieldComponent } from "@odx/angular/components/form-field";
import { distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";

@Component({
  selector: "app-project-form",
  templateUrl: "./project-form.component.html",
  styleUrls: ["./project-form.component.scss"],
  imports: [
    AsyncPipe,
    UiKitModule,
    ReactiveFormsModule,
    AreaHeaderComponent,
    FormFieldComponent,
    MapCountryNamePipe,
    TranslateProjectStatusPipe,
    ForzaIdFormFieldComponent,
    DraegerContactPersonFormFieldComponent,
    OpportunityLinkFormFieldComponent,
    PlaceNameFormFieldComponent,
    ForzaLinkFormFieldComponent,
    TranslateProjectRegionPipe,
  ],
  standalone: true,
})
export class ProjectFormComponent {
  private formBuilder = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private projectStatusService = inject(ProjectStatusService);
  private projectFormsService = inject(ProjectFormsService);
  private userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);

  isReadonly: InputSignal<boolean> = input.required<boolean>();
  templateFields = input<TemplateProjectDto>();
  statusSignal = signal<string | undefined>("");
  clearFormSignal = input<boolean>(false);
  projectSignal = input<ScopedProject | undefined>(undefined);
  userSignal = toSignal(this.userService.user$);
  projectCountry = signal("");

  localProjectSignal = toSignal(
    toObservable(this.projectSignal).pipe(
      switchMap((project) => (project ? this.projectService.getLocalProjectById(project.id) : of(undefined))),
      startWith(undefined),
    ),
    { initialValue: undefined },
  );

  isProjectCountryFixed = computed(() => !!this.localProjectSignal());
  isForzaIdVisible = computed(() => {
    const templateFields = this.templateFields();
    if (!templateFields) return false;
    return !!templateFields?.details?.information?.forzaId?.visible;
  });

  private userLocaleService = inject(UserLocaleService);

  user$ = this.userService.user$;

  projectInitialized = false;

  projectStatuses$: Observable<ProjectStatusDto[]> = this.userService.businessScope$.pipe(
    switchMap((scope) => this.projectStatusService.getProjectStatuses(scope)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected projectStatus: string = "";

  projectForm!: FormGroup;

  @Output() projectFormEmitter$ = new EventEmitter<FormGroup>();
  @Output() countryChange = new EventEmitter<string>();

  @ViewChild("submitButton")
  submitButton?: ElementRef;

  protected countries$: Observable<Country[]>;

  private supportedCountryCodes: string[] = [];
  private supportedCountriesLoaded = false;

  protected projectRegion?: ProjectRegion;
  protected regionOptions?: ProjectRegion[];

  protected readonly navigator = navigator;

  protected isForzaIdRequired!: boolean;
  protected isPlaceNameVisible!: boolean;
  protected isPlaceNameRequired!: boolean;
  protected isRegionVisible!: boolean;
  protected isRegionRequired!: boolean;
  protected isDraegerContactPersonVisible!: boolean;
  protected isDraegerContactPersonRequired!: boolean;
  protected isOpportunityIdVisible!: boolean;
  protected isOpportunityIdRequired!: boolean;
  protected isOpportunityLinkRequired!: boolean;

  private isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
  isReadOnlyUser = toSignal(this.isReadOnlyUser$);
  isWriteable = model(false);

  constructor() {
    effect(() => {
      this.isReadonly();
      this.isReadOnlyUser();
      const result = !this.isReadOnlyUser() && !this.isReadonly();
      this.isWriteable.update(() => result);
    });
    this.projectStatuses$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((statuses) => {
      if (this.statusSignal() === "") {
        const initialStatus = statuses.find((status) => status.initial)?.text ?? "";
        this.statusSignal.set(initialStatus);
      }
    });
    this.countries$ = this.userLocaleService.supportedCountries$;
    this.countries$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((countries) => {
      this.supportedCountryCodes = countries.map((c) => c.countryCode);
      this.supportedCountriesLoaded = true;
      this.applyUserCountryIfSupported();
      this.resetCountryIfUnsupported();
    });

    this.projectFormsService.projectInitialized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project) => {
      if (!project || !this.projectForm) return;
      this.initForm(project);
    });

    this.projectFormsService.projectReset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project) => {
      if (!this.projectForm) return;
      this.projectForm.reset();
      this.initForm(project);
    });

    effect(() => {
      const user = this.userSignal();
      const status = this.statusSignal();
      const project = this.projectSignal();
      const templateFields = this.templateFields();

      if (!user) return;
      if (!this.projectForm) {
        this.createForm();
        this.applyUserCountryIfSupported();
        if (templateFields) this.applyTemplate(templateFields);
      }
      this.projectForm.patchValue({ status: status });
      this.projectFormEmitter$.emit(this.projectForm);
      if (project) {
        if (templateFields) this.applyTemplate(templateFields);
        this.isProjectCountryFixed();
        this.projectCountry.update(() => project.country ?? "");
        this.initForm(project);
      }
    });

    effect(() => {
      const clearForm: boolean = this.clearFormSignal();
      if (this.projectForm && clearForm) {
        this.projectForm.reset();
      }
    });

    effect(() => {
      const template = this.templateFields();
      if (this.projectForm && template) {
        this.applyTemplate(template);
      }
    });
  }

  private resetCountryIfUnsupported() {
    if (!this.supportedCountryCodes.length || this.isProjectCountryFixed()) return;

    const countryControl = this.projectForm?.get("country");

    if (countryControl && !this.supportedCountryCodes.includes(countryControl.value)) {
      countryControl.setValue(null);
    }
  }

  private applyUserCountryIfSupported() {
    if (!this.supportedCountriesLoaded || this.isProjectCountryFixed()) return;

    const user = this.userSignal();
    const countryControl = this.projectForm?.get("country");

    if (!user || !countryControl || countryControl.value) return;
    if (!this.supportedCountryCodes.includes(user.country)) return;

    countryControl.setValue(user.country);
  }

  private createForm() {
    this.projectForm = this.formBuilder.group({
      name: [null, Validators.required],
      version: [],
      status: [this.statusSignal()],
      forzaId: [],
      placeName: [],
      country: [null, Validators.required],
      region: [],
      draegerContactPerson: [],
      opportunityId: [],
      opportunityLink: [],
      notes: [],
    });
    this.resetCountryIfUnsupported();
    const countryControl = this.projectForm.get("country");

    if (countryControl) {
      countryControl.valueChanges
        .pipe(
          startWith(countryControl.value),
          filter((country): country is string => !!country),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((country) => this.countryChange.emit(country));
    }
  }

  private initForm(project: ProjectFormInitialization) {
    if (!this.projectForm) return;
    this.projectInitialized = true;
    const isScopedProject = this.isScopedProject(project);
    this.statusSignal.update(() => project.status);
    this.projectRegion = isFgdsProject(project) ? project.region : undefined;
    this.projectForm.patchValue({
      name: project.name,
      status: this.statusSignal(),
      country: project.country ?? null,
      opportunityId: project.opportunityId,
      opportunityLink: project.opportunityLink,
      version: isScopedProject ? project.version : undefined,
      region: isFgdsProject(project) ? project.region : undefined,
      placeName: isScopedProject ? project.placeName : undefined,
      forzaId: isFgdsProject(project) ? project.forzaId : undefined,
      draegerContactPerson: isMonitoringProject(project) ? project.draegerContactPerson : undefined,
      notes: isScopedProject ? project.notes : undefined,
    });
    this.resetCountryIfUnsupported();
    this.projectFormEmitter$.emit(this.projectForm);
  }

  triggerSubmit() {
    this.projectRegion = this.projectForm.value.region;
    this.submitButton?.nativeElement.click();
  }

  private isScopedProject(project: ScopedProject | ProjectFormInitialValues): project is ScopedProject {
    return isFgdsProject(project) || isMonitoringProject(project);
  }

  private applyTemplate(template: TemplateProjectDto | undefined) {
    if (!template) return;
    if (!this.projectForm) return;
    const information = template.details?.information;

    this.isForzaIdRequired = information?.forzaId?.required ?? false;

    this.isPlaceNameVisible = information?.placeName?.visible ?? false;
    this.isPlaceNameRequired = information?.placeName?.required ?? false;

    this.isRegionVisible = information?.region?.visible ?? false;
    this.isRegionRequired = information?.region?.required ?? false;
    this.regionOptions = template.regions as ProjectRegion[];

    this.isDraegerContactPersonVisible = information?.draegerContactPerson?.visible ?? false;
    this.isDraegerContactPersonRequired = information?.draegerContactPerson?.required ?? false;

    this.isOpportunityIdVisible = information?.opportunityId?.visible ?? false;
    this.isOpportunityIdRequired = information?.opportunityId?.required ?? false;
    this.isOpportunityLinkRequired = information?.opportunityLink?.required ?? false;

    this.updateControl("forzaId", this.isForzaIdVisible(), this.isForzaIdRequired, [Validators.pattern("[0-9]{5}")]);
    this.updateControl("placeName", this.isPlaceNameVisible, this.isPlaceNameRequired);
    this.updateControl("region", this.isRegionVisible, this.isRegionRequired);
    this.updateControl("draegerContactPerson", this.isDraegerContactPersonVisible, this.isDraegerContactPersonRequired, [
      Validators.email,
    ]);
    this.updateControl("opportunityId", this.isOpportunityIdVisible, this.isOpportunityIdRequired);
    this.projectFormEmitter$.emit(this.projectForm);
  }

  private updateControl(controlName: string, visible: boolean, required: boolean, validators: any[] = []) {
    const control = this.projectForm.get(controlName);
    if (!control) return;

    if (!visible) {
      control.clearValidators();
      control.reset();
      control.updateValueAndValidity();
      return;
    }

    const nextValidators = required ? [Validators.required, ...validators] : validators;
    control.setValidators(nextValidators.length ? nextValidators : null);
    control.updateValueAndValidity();
  }
}
